import apiClient, { extractData, handleApiError } from './api.js';

/**
 * Actual ledger API response shape (from /ledger/date-range):
 * {
 *   ledger_entry_id: string,
 *   date: "YYYY-MM-DD",
 *   outlet_id: string,
 *   total_sales: number,
 *   number_of_orders: number,
 *   created_at: string (ISO)
 * }
 *
 * Each row is a DAILY SUMMARY — not an individual transaction.
 */

export class LedgerService {
  /**
   * Fetch ledger entries for a date range.
   * GET /api/v1/ledger/date-range?outlet_id=...&start_date=...&end_date=...
   *
   * @param {string} outletId
   * @param {Date|string} startDate
   * @param {Date|string} endDate
   * @param {number} [limit]
   * @returns {Promise<Array>} array of daily summary objects
   */
  static async fetchLedgerForDateRange(outletId, startDate, endDate, limit) {
    try {
      const params = { outlet_id: outletId };

      if (startDate) {
        params.start_date = startDate instanceof Date
          ? startDate.toISOString().split('T')[0]
          : startDate;
      }
      if (endDate) {
        params.end_date = endDate instanceof Date
          ? endDate.toISOString().split('T')[0]
          : endDate;
      }
      if (limit) params.limit = limit;

      const response = await apiClient.get('/ledger/date-range', { params });
      return extractData(response) || [];
    } catch (error) {
      console.error('Error fetching ledger for date range:', error);
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Fetch ledger data for the last 7 days
   * @param {string} outletId
   * @returns {Promise<Array>}
   */
  static async fetchLast7DaysLedger(outletId) {
    const endDate = new Date();
    endDate.setDate(endDate.getDate() + 1); // exclusive end
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 6);

    return this.fetchLedgerForDateRange(outletId, startDate, endDate);
  }

  /**
   * Fetch ledger data for a specific week (with offset)
   * @param {string} outletId
   * @param {number} [weekOffset=0]
   * @returns {Promise<Array>} sorted daily summaries
   */
  static async fetchWeekLedger(outletId, weekOffset = 0) {
    const today = new Date();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay() + (weekOffset * 7));
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 7);

    const entries = await this.fetchLedgerForDateRange(outletId, startOfWeek, endOfWeek);

    // Map to the shape expected by useLedger / ledgerStore consumers
    // (PascalCase / camelCase aliases alongside raw snake_case fields)
    return entries
      .map((e) => ({
        ...e,
        Date: e.date,
        totalSales: e.total_sales || 0,
        numberOfOrders: e.number_of_orders || 0,
      }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }

  /**
   * Fetch monthly ledger data
   * @param {string} outletId
   * @param {number} [monthOffset=0]
   * @returns {Promise<Array>}
   */
  static async fetchMonthlyLedger(outletId, monthOffset = 0) {
    const today = new Date();
    const startOfMonth = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1);
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + monthOffset + 1, 1);

    return this.fetchLedgerForDateRange(outletId, startOfMonth, endOfMonth);
  }

  /**
   * Get aggregate statistics for a date range.
   * Computes totals from daily summary rows.
   *
   * @param {string} outletId
   * @param {Date} startDate
   * @param {Date} endDate
   * @returns {Promise<Object>}
   */
  static async getLedgerStatistics(outletId, startDate, endDate) {
    try {
      const entries = await this.fetchLedgerForDateRange(outletId, startDate, endDate);

      let totalSales = 0;
      let totalOrderCount = 0;

      entries.forEach((entry) => {
        totalSales += entry.total_sales || 0;
        totalOrderCount += entry.number_of_orders || 0;
      });

      const totalDays = Math.max(1, Math.ceil(
        (new Date(endDate).getTime() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24)
      ));

      return {
        totalSales,
        totalOrderCount,
        averageDailySales: totalSales / totalDays,
        totalEntries: entries.length,
      };
    } catch (error) {
      console.error('Error getting ledger statistics:', error);
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Get sales trend data (daily sales for chart).
   * Fills in zeroes for dates with no entries.
   *
   * @param {string} outletId
   * @param {Date} startDate  – inclusive
   * @param {Date} endDate    – inclusive
   * @returns {Promise<{labels: string[], salesData: number[], ordersData: number[]}>}
   */
  static async getSalesTrendData(outletId, startDate, endDate) {
    try {
      // Fetch with +1 day on end to be inclusive
      const fetchEnd = new Date(endDate);
      fetchEnd.setDate(fetchEnd.getDate() + 1);

      const entries = await this.fetchLedgerForDateRange(outletId, startDate, fetchEnd);

      // Index entries by date string for O(1) lookup
      const dataMap = new Map();
      entries.forEach((entry) => {
        const dateStr = entry.date ? entry.date.split('T')[0] : '';
        if (dateStr) {
          dataMap.set(dateStr, {
            sales: entry.total_sales || 0,
            orders: entry.number_of_orders || 0,
          });
        }
      });

      // Generate every date in the range
      const labels = [];
      const salesData = [];
      const ordersData = [];

      const cursor = new Date(startDate);
      cursor.setHours(0, 0, 0, 0);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);

      while (cursor <= end) {
        const dateStr = cursor.toISOString().split('T')[0];
        const data = dataMap.get(dateStr) || { sales: 0, orders: 0 };

        labels.push(dateStr);
        salesData.push(data.sales);
        ordersData.push(data.orders);

        cursor.setDate(cursor.getDate() + 1);
      }

      return { labels, salesData, ordersData };
    } catch (error) {
      console.error('Error getting sales trend data:', error);
      throw new Error(handleApiError(error));
    }
  }
}
